import { Router, useLocation } from "@reach/router"
import { differenceInDays } from "date-fns"
import { navigate } from "gatsby"
import { memo, useCallback, useEffect } from "react"

import { ABTestContainer, AB_TESTS } from "@trueskin-web/ab-experiments"
import {
  consultationClient,
  customerIoClient,
  orderClient,
  useQuery,
  userClient,
} from "@trueskin-web/apis"
import {
  Container,
  RouteGuard as GlobalRouteGuard,
  Seo,
  Spinner,
  useQueryState,
} from "@trueskin-web/components"
import { useAuth } from "@trueskin-web/context"
import { envDetails } from "@trueskin-web/core"
import {
  initialConsultationService,
  storageService,
  trackingService,
} from "@trueskin-web/services"
import { Routes } from "@trueskin-web/translations"

import { SignupV1, SignupV2 } from "../ab-experiments/signup-step-redesign"
import {
  SubscriptionTiersPayment,
  SubscriptionTiersV1,
  SubscriptionTiersV2,
} from "../ab-experiments/subscription-tiers"
import ChoosePlan from "../components/choose-plan"
import RouteChangeTracker from "../hooks/route-change-tracker"
import Layout from "../layout"

import AppSelectionScreen from "./bd-welcome-screen"
import CreateAccount from "./create-account"
import DocumentsUpload from "./documents"
import Payment from "./payment"
import Photos from "./photos"
import Questionnaire from "./questionnaire"
import Shipping from "./shipping"
import TreatmentPlanNavigation from "./treatment-plan-navigation"
import WelcomeBack from "./welcome-back"

const storageKeys = storageService.getStorageKeys()
const storageTypes = storageService.getStorageTypes()

const Redirect = () => {
  const { user } = useAuth()

  const { getSearchString } = useQueryState()
  const {
    subscription,
    billingAddresses: [billingAddress] = [],
    redoInitialQuestionnaire = false,
  } = user || {}

  const {
    consultations: [{ type: consultationType, status: consultationStatus }] = [
      {},
    ],
  } = subscription || {}

  const initialTreatmentPlanRedirectDisabled = storageService.getItem({
    id: storageKeys.DISABLE_INITIAL_TREATMENT_PLAN_REDIRECT,
    storageType: storageTypes.SESSION,
  })

  const {
    isLoading: isCurrentOrderSummaryLoading,
    data: { data: { lineItems: currentSummaryItems = [] } = {} } = {},
  } = useQuery("currentOrderSummary", orderClient.getCurrentSummary, {
    enabled: !!user,
  })

  const { data: healthData, isLoading: isLoadingHealthData } = useQuery(
    "healthRecord",
    userClient.getHealthRecord,
    {
      enabled: !!user && !initialTreatmentPlanRedirectDisabled,
    }
  )

  const isReturningUser =
    !!user && differenceInDays(new Date(), new Date(user.createdAt)) > 0

  useEffect(() => {
    return () => {
      if (user) {
        storageService.removeItem({
          id: storageKeys.DISABLE_INITIAL_TREATMENT_PLAN_REDIRECT,
          storageType: storageTypes.SESSION,
        })
      }
    }
  }, [user])

  // TODO: Remove VWO-related code once it has been implemented in production or when the decision to remove it has been made
  useEffect(() => {
    if (subscription && consultationStatus === "created") {
      storageService.removeItem({
        id: storageKeys.VWO_THEME_VARIANT,
      })
    }
  }, [subscription, consultationStatus])

  if (isCurrentOrderSummaryLoading || isLoadingHealthData) {
    return <Spinner isFullPage />
  }

  if (!user) {
    navigate(Routes.App.Start.Questionnaire.url + getSearchString(), {
      replace: true,
    })
    return null
  }

  if (!subscription) {
    if (redoInitialQuestionnaire || initialConsultationService.isEmpty()) {
      navigate(Routes.App.Start.Questionnaire.url, { replace: true })
      return null
    }

    if (isReturningUser) {
      navigate(Routes.App.Start.WelcomeBack.url, { replace: true })

      return null
    }

    if (healthData && !initialTreatmentPlanRedirectDisabled) {
      navigate(`${Routes.App.Start.InitialTreatmentPlan.url}`, {
        replace: true,
      })
      return null
    }

    if (!currentSummaryItems?.length) {
      navigate(Routes.App.Start.ChoosePlan.url, { replace: true })
      return null
    }

    if (!billingAddress || user.isDummy) {
      navigate(Routes.App.Start.Shipping.url, { replace: true })
      return null
    }

    navigate(Routes.App.Start.Payment.url, { replace: true })
    return null
  }

  if (
    consultationType === "initial" &&
    consultationStatus === "waitingfileupload"
  ) {
    if (storageService.getItem({ id: storageKeys.BVDD_PRAXIS })) {
      navigate(Routes.App.Start.Documents.url, { replace: true })
    } else {
      navigate(Routes.App.Start.Photos.url, { replace: true })
    }
    return null
  }

  navigate(Routes.App.url, { replace: true })
  return null
}

const RouteGuard = (props) => (
  <GlobalRouteGuard fallback={Redirect} {...props} />
)

const Start = () => {
  const location = useLocation()

  const { user } = useAuth()

  const {
    userType,
    redoInitialQuestionnaire,
    subscription,
    billingAddresses: [billingAddress] = [],
  } = user || {}

  const {
    consultations: [{ type: consultationType, status: consultationStatus }] = [
      {},
    ],
  } = subscription || {}

  const {
    isLoading: isCurrentOrderSummaryLoading,
    data: { data: { lineItems: currentSummaryItems = [] } = {} } = {},
  } = useQuery("currentOrderSummary", orderClient.getCurrentSummary, {
    enabled: !!user,
  })

  const { isLoading: isInitialConsultationLoading } = useQuery(
    "initialConsultation",
    consultationClient.getInitialConsultation,
    {
      enabled: !!user && initialConsultationService.isEmpty(),
      onSuccess: initialConsultationService.save,
    }
  )

  const canAccessQuestionnaire = useCallback(
    () =>
      !user ||
      user?.redoInitialQuestionnaire ||
      initialConsultationService.isEmpty(),
    [user]
  )

  const canAccessCreateAccount = useCallback(
    ({ token }) => token && !user,
    [user]
  )

  const canAccessInitialTreatmentPlan = useCallback(() => {
    return user && !subscription
  }, [user, subscription])

  const canAccessChoosePlan = useCallback(
    () => user && !subscription && !user.redoInitialQuestionnaire,
    [subscription, user]
  )

  const canAccessShipping = useCallback(
    () =>
      user &&
      !subscription &&
      currentSummaryItems.length > 0 &&
      !user.redoInitialQuestionnaire,
    [subscription, user, currentSummaryItems]
  )

  const canAccessPayment = useCallback(
    async () =>
      !subscription &&
      billingAddress &&
      currentSummaryItems.length > 0 &&
      !user.redoInitialQuestionnaire &&
      !user.isDummy,
    [billingAddress, currentSummaryItems, subscription, user]
  )

  const canAccessPhotos = useCallback(
    () =>
      consultationType === "initial" &&
      consultationStatus === "waitingfileupload",
    [consultationStatus, consultationType]
  )

  const canAccessDocuments = useCallback(
    () =>
      consultationType === "initial" &&
      consultationStatus === "waitingfileupload" &&
      storageService.getItem({ id: storageKeys.BVDD_PRAXIS }),
    [consultationType, consultationStatus]
  )

  // TODO @fox: implement differently
  const canAccessTofuWelcome = useCallback(
    () =>
      userType === "tofu" &&
      !redoInitialQuestionnaire &&
      initialConsultationService.isEmpty(),
    [userType, redoInitialQuestionnaire]
  )

  const canAccessWelcomeBack = useCallback(() => {
    const isReturningUser =
      !!user && differenceInDays(new Date(), new Date(user.createdAt)) > 0

    return isReturningUser && !subscription
  }, [user, subscription])

  const onRouteChangeHandler = useCallback(
    (currentPath) => {
      const originalSource = trackingService.getOriginalSource()
      const touchpoints = trackingService.getAllTrackingTouchPoints()
      const latestTouchpoint = touchpoints[touchpoints.length - 1]

      if (!originalSource && latestTouchpoint) {
        trackingService.setOriginalSource({
          ...latestTouchpoint,
          timestamp: Math.round(Date.now() / 1000),
        })
      }

      if (!!user) {
        customerIoClient.track(customerIoClient.EVENT_NAMES.PAGE_VIEW, {
          id: user.accountId,
          email: user.email,
          timestamp: Math.round(Date.now() / 1000),
          visited_url: currentPath,
          source: originalSource || {},
        })
      }
    },
    [user]
  )

  const onPageLeaveHandler = useCallback(
    (currentPath) => {
      if (!!user) {
        customerIoClient.track(customerIoClient.EVENT_NAMES.PAGE_LEAVE, {
          id: user.accountId,
          email: user.email,
          timestamp: Math.round(Date.now() / 1000),
          visited_url: currentPath,
        })
      }
    },
    [user]
  )

  if (isCurrentOrderSummaryLoading || isInitialConsultationLoading) {
    return <Spinner isFullPage />
  }

  return (
    <Layout enableConfirmation>
      {(location.pathname === Routes.App.Start.url ||
        location.pathname === Routes.App.Start.Questionnaire.url) && (
        <Seo
          title=""
          meta={[{ key: "robots", name: "robots", content: "noindex" }]}
        />
      )}

      <RouteChangeTracker
        onRouteChange={onRouteChangeHandler}
        onPageLeave={onPageLeaveHandler}
      />

      <Container>
        <Router>
          {!["main", "master"].includes(envDetails.gitBranch) && (
            <EnvDetails path={"/env"} />
          )}
          <RouteGuard
            path={Routes.App.Start.Questionnaire.path}
            condition={canAccessQuestionnaire}
            component={Questionnaire}
          />
          {/* TODO @julianna: clean up after A/B test is concluded AG-609 */}
          <RouteGuard
            path={`${Routes.App.Start.CreateAccount.path}/:token`}
            condition={canAccessCreateAccount}
            component={AbTestSignupContainer}
          />
          <RouteGuard
            path={Routes.App.Start.InitialTreatmentPlan.path}
            condition={canAccessInitialTreatmentPlan}
            component={TreatmentPlanNavigation}
          />

          {/* TODO @julianna: clean up after A/B test is concluded AG-468 */}
          <RouteGuard
            path={Routes.App.Start.ChoosePlan.path}
            condition={canAccessChoosePlan}
            component={AbTestSubscriptionTiersContainer}
          />

          <RouteGuard
            path={Routes.App.Start.Shipping.path}
            condition={canAccessShipping}
            component={Shipping}
          />

          {/* TODO @julianna: clean up after A/B test is concluded AG-468 */}
          <RouteGuard
            path={Routes.App.Start.Payment.path}
            condition={canAccessPayment}
            component={AbTestSubscriptionTiersPaymentContainer}
          />

          <RouteGuard
            path={Routes.App.Start.Documents.path}
            condition={canAccessDocuments}
            component={DocumentsUpload}
          />
          <RouteGuard
            path={Routes.App.Start.Photos.path}
            condition={canAccessPhotos}
            component={Photos}
          />
          <RouteGuard
            path={Routes.App.Start.WelcomeBack.path}
            condition={canAccessWelcomeBack}
            component={WelcomeBack}
          />
          {/* TODO @fox: make sure to move/remove this route from here*/}
          <RouteGuard
            path={Routes.App.Start.TofuWelcome.path}
            condition={canAccessTofuWelcome}
            component={AppSelectionScreen}
          />
          <Redirect default />
        </Router>
      </Container>
    </Layout>
  )
}

// TODO @julianna: clean up after A/B test is concluded AG-609
const AbTestSignupContainer = memo(() => (
  <ABTestContainer
    testName={AB_TESTS.SignupStepRedesign}
    variants={[
      [1, SignupV1],
      [2, SignupV2],
    ]}
    fallbackComponent={CreateAccount}
  />
))

// TODO @julianna: clean up after A/B test is concluded AG-568
const AbTestSubscriptionTiersContainer = memo(() => (
  <ABTestContainer
    testName={AB_TESTS.SubscriptionTiers}
    variants={[
      [1, SubscriptionTiersV1],
      [2, SubscriptionTiersV2],
    ]}
    fallbackComponent={ChoosePlan}
  />
))

// TODO @julianna: clean up after A/B test is concluded AG-568
const AbTestSubscriptionTiersPaymentContainer = memo(() => (
  <ABTestContainer
    testName={AB_TESTS.SubscriptionTiers}
    variants={[
      [1, SubscriptionTiersPayment],
      [2, SubscriptionTiersPayment],
    ]}
    fallbackComponent={Payment} // TODO: change to ChoosePlan when the experiment is concluded
  />
))

// TODO @julianna: clean up after A/B test is concluded AG-609
AbTestSignupContainer.displayName = "AbTestSignupContainer"
AbTestSubscriptionTiersContainer.displayName =
  "AbTestSubscriptionTiersContainer"
AbTestSubscriptionTiersPaymentContainer.displayName =
  "AbTestSubscriptionTiersPaymentContainer"

const EnvDetails = () => {
  return <pre>{JSON.stringify(envDetails)}</pre>
}

export default Start
